import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useCustomerGroups, useSalesChannels } from '../../hooks';
import type { CreateMarkdownPlanRequest, MarkdownAdjustmentType } from '../../model/markdownPlanTypes';
import type { Product } from '../../model/types';
import { ProductMultiPicker } from './ProductMultiPicker';

interface StepDraft {
  startsAt: string;
  adjustmentType: MarkdownAdjustmentType;
  adjustmentValue: string;
}

const EMPTY_STEP: StepDraft = { startsAt: '', adjustmentType: 'PERCENT_OFF_BASE', adjustmentValue: '' };

export interface MarkdownPlanFormDialogProps {
  open: boolean;
  pending?: boolean;
  onClose: () => void;
  onSave: (body: CreateMarkdownPlanRequest) => void;
}

/**
 * Markdown-plan create dialog (design spec §8.9) — no update endpoint exists on the live BFF
 * contract, so this is the ONLY way to author a plan's steps; there is no later edit. Adds an
 * optional customer-group scope alongside the spec mock's channel scope (the schema
 * `CreateMarkdownPlanRequest.customerGroupCode` supports it and `PriceChangeBatchWizardDialog`
 * exposes the same axis — omitting it here would silently make plans unable to target a
 * group-scoped clearance).
 */
export function MarkdownPlanFormDialog({ open, pending = false, onClose, onSave }: MarkdownPlanFormDialogProps) {
  const { t } = useTranslation('catalog');
  const [name, setName] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [channelCode, setChannelCode] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [steps, setSteps] = useState<StepDraft[]>([{ ...EMPTY_STEP }]);

  const channelsQuery = useSalesChannels();
  const groupsQuery = useCustomerGroups();
  const channels = (channelsQuery.data ?? []).filter((c) => c.active);
  const groups = (groupsQuery.data ?? []).filter((g) => g.active);

  const reset = () => {
    setName('');
    setProducts([]);
    setChannelCode('');
    setGroupCode('');
    setSteps([{ ...EMPTY_STEP }]);
  };

  const handleClose = () => {
    if (pending) return;
    reset();
    onClose();
  };

  const updateStep = (index: number, patch: Partial<StepDraft>) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const addStep = () => setSteps((prev) => [...prev, { ...EMPTY_STEP }]);
  const removeStep = (index: number) => setSteps((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const parsedDates = steps.map((s) => (s.startsAt ? new Date(s.startsAt).getTime() : NaN));
  const datesAscending = parsedDates.every((d, i) => !Number.isNaN(d) && (i === 0 || d > parsedDates[i - 1]));
  const stepsValid =
    steps.length > 0 &&
    steps.every((s) => s.startsAt.trim() !== '' && s.adjustmentValue.trim() !== '' && !Number.isNaN(Number.parseFloat(s.adjustmentValue)) && Number.parseFloat(s.adjustmentValue) > 0) &&
    datesAscending;
  const valid = name.trim() !== '' && products.length > 0 && stepsValid;

  const handleSave = () => {
    onSave({
      name: name.trim(),
      channelCode: channelCode || undefined,
      customerGroupCode: groupCode || undefined,
      productIds: products.map((p) => p.id),
      steps: steps.map((s, i) => ({
        seq: i + 1,
        startsAt: new Date(s.startsAt).toISOString(),
        adjustmentType: s.adjustmentType,
        adjustmentValue: Number.parseFloat(s.adjustmentValue),
      })),
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t('markdownPlansPanel.create')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField label={t('markdownPlansPanel.nameLabel')} required value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus />
          <ProductMultiPicker value={products} onChange={setProducts} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label={t('markdownPlansPanel.channelScopeLabel')}
              value={channelCode}
              onChange={(e) => setChannelCode(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">{t('markdownPlansPanel.scopeAllChannels')}</MenuItem>
              {channels.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            {groups.length > 0 ? (
              <TextField
                select
                label={t('markdownPlansPanel.groupScopeLabel')}
                value={groupCode}
                onChange={(e) => setGroupCode(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">{t('markdownPlansPanel.scopeAllCustomers')}</MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.code} value={g.code}>
                    {g.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
          </Stack>

          <Typography variant="subtitle2" fontWeight={600}>
            {t('markdownPlansPanel.stepsTitle')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('markdownPlansPanel.steps.seq')}</TableCell>
                <TableCell>{t('markdownPlansPanel.steps.startsAt')}</TableCell>
                <TableCell>{t('markdownPlansPanel.steps.type')}</TableCell>
                <TableCell>{t('markdownPlansPanel.steps.value')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {steps.map((step, index) => {
                const dateInvalid = step.startsAt !== '' && (Number.isNaN(parsedDates[index]) || (index > 0 && parsedDates[index] <= parsedDates[index - 1]));
                return (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        type="datetime-local"
                        size="small"
                        value={step.startsAt}
                        onChange={(e) => updateStep(index, { startsAt: e.target.value })}
                        error={dateInvalid}
                        helperText={dateInvalid ? t('markdownPlansPanel.steps.dateOrderError') : undefined}
                        slotProps={{
                          inputLabel: { shrink: true },
                          htmlInput: {
                            'aria-label': t('markdownPlansPanel.steps.startsAtAria', { seq: index + 1 }),
                            'data-testid': `markdown-step-date-${index}`,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={step.adjustmentType}
                        onChange={(e) => updateStep(index, { adjustmentType: e.target.value as MarkdownAdjustmentType })}
                        sx={{ minWidth: 170 }}
                        slotProps={{ select: { 'aria-label': t('markdownPlansPanel.steps.typeAria', { seq: index + 1 }) } }}
                      >
                        <MenuItem value="PERCENT_OFF_BASE">{t('markdownPlansPanel.steps.percentOffBase')}</MenuItem>
                        <MenuItem value="SET_AMOUNT">{t('markdownPlansPanel.steps.setAmount')}</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={step.adjustmentValue}
                        onChange={(e) => updateStep(index, { adjustmentValue: e.target.value })}
                        slotProps={{
                          htmlInput: {
                            min: 0,
                            step: '0.01',
                            'aria-label': t('markdownPlansPanel.steps.valueAria', { seq: index + 1 }),
                          },
                        }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" aria-label={t('markdownPlansPanel.steps.removeAria', { seq: index + 1 })} onClick={() => removeStep(index)} disabled={steps.length === 1}>
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Button startIcon={<AddRoundedIcon />} onClick={addStep} variant="text" size="small" sx={{ alignSelf: 'flex-start' }}>
            {t('markdownPlansPanel.steps.add')}
          </Button>

          <Alert severity="info" icon={false}>
            {t('markdownPlansPanel.materializeNote')}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={handleSave}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
